/**
 * usePitchSession.ts — React binding for the pure session machine (GOAL.md task #2).
 *
 * Drives the framework-free `sessionReducer` (task #1) and wires its inputs to the
 * real world: native RPC (`native.invoke` / `native.on`) on the app shell, or a mock
 * `PracticeBridge` over `createMockPitchTrackerModule` in the browser. Same flow on
 * both paths — only the command transport differs.
 *
 * Race safety (the whole point): the machine's `sessionCan` guard is evaluated
 * SYNCHRONOUSLY against a ref-held status before any command fires, so a multi-click
 * (or a conflicting start↔stop) during an in-flight transition is dropped before it
 * can reach native. A `confirmTimeoutMs` safety timer un-wedges a pending state if the
 * native confirmation never arrives (no permanent lock).
 *
 * Invariants (GOAL.md): every native call goes through `native` with a mock browser
 * fallback; no raw PCM; the machine itself stays React/native-free.
 */
import { useEffect, useRef, useState } from 'react';

import {
  type SessionCommand,
  type SessionGuards,
  type SessionInput,
  type SessionMachineState,
  type SessionStatus,
  initialSessionState,
  sessionCan,
  sessionReducer,
} from '../session';
import {
  createMockPitchTrackerModule,
  createPitchTrackerConfig,
} from '../types';
import {
  type PracticeBridge,
  createPracticeBridge,
  forwardToNativeChannel,
  isRunningInNativeApp,
  native,
} from '../utils';

import type {
  PitchTrackerConfig,
  PitchTrackerEvent,
  PitchTrackerModule,
} from '../types';

/** Coarse, UI-facing grouping of the machine status (drives spinner / labels). */
export type SessionPhase =
  | 'stopped'
  | 'active'
  | 'paused'
  | 'transitioning'
  | 'error';

export interface UsePitchSessionOptions {
  /** Config used by `start()` when none is passed to it. Defaults to the mock config. */
  config?: PitchTrackerConfig;
  /** ms to wait for a native confirmation before a safe fallback fires. Default 4000. */
  confirmTimeoutMs?: number;
  /** Force the native/browser path (tests); defaults to runtime detection. */
  isNative?: boolean;
  /** Browser-path module factory (tests inject a controllable mock). */
  createModule?: () => PitchTrackerModule;
  /** Clock for the fps window; defaults to `Date.now`. */
  now?: () => number;
}

export interface UsePitchSession {
  state: SessionStatus;
  phase: SessionPhase;
  can: SessionGuards;
  start: (config?: PitchTrackerConfig) => void;
  stop: () => void;
  pause: () => void;
  resume: () => void;
  /** Analysis frames received in the last second. */
  fps: number;
  captureSampleRate: number | null;
  isNative: boolean;
}

const DEFAULT_CONFIRM_TIMEOUT_MS = 4_000;

/**
 * `start` 만 다른 시계를 쓴다. 마이크 권한 대화상자는 네이티브 `start()` 안에서
 * 뜨고(ADR-0004, `apps/mobile/src/utils/practice.ts`), 그동안 확인 이벤트가 오지
 * 않는다 — 즉 첫 실행의 응답 시간은 사람이 '허용'을 누르는 시간이다. 4초로 재면
 * 사용자가 대화상자를 읽는 동안 세션이 스스로 멈춘 것으로 되돌아간다.
 *
 * 권한이 이미 있으면 start 는 밀리초 안에 확인되므로 평소 화면은 그대로다.
 * 길어지는 것은 네이티브가 정말 응답하지 않을 때 멈춰 보이는 시간뿐이고, 그
 * 경우를 4초 만에 포기하는 것보다 첫 실행이 되는 편이 낫다.
 */
const START_CONFIRM_TIMEOUT_MS = 60_000;

/**
 * 같은 이유로 브릿지 호출도 기본값(5초 타임아웃 + 3회 재시도)을 쓰면 안 된다.
 * 재시도는 권한 대화상자가 떠 있는 채로 `startPractice` 를 다시 보내고,
 * 네이티브는 그때마다 브릿지를 버리고 엔진을 새로 띄운다.
 */
const START_INVOKE_OPTIONS = {
  timeout: START_CONFIRM_TIMEOUT_MS,
  retries: 1,
} as const;
const FPS_WINDOW_MS = 1_000;

const PHASE_BY_STATUS: Record<SessionStatus, SessionPhase> = {
  idle: 'stopped',
  starting: 'transitioning',
  running: 'active',
  pausing: 'transitioning',
  paused: 'paused',
  resuming: 'transitioning',
  stopping: 'transitioning',
  error: 'error',
};

/** The pending status each command parks in until its confirmation lands. */
const PENDING_BY_COMMAND: Record<SessionCommand, SessionStatus> = {
  start: 'starting',
  stop: 'stopping',
  pause: 'pausing',
  resume: 'resuming',
};

/**
 * Safe fallback fed to the machine when a command's confirmation never arrives.
 * start/stop drop to idle (don't claim a session we couldn't confirm started);
 * pause/resume settle optimistically — a stray `pitch_frame` re-syncs to running.
 */
const RECOVERY_INPUT: Record<SessionCommand, SessionInput> = {
  start: {
    kind: 'event',
    event: { type: 'session_stopped', reason: 'unknown' },
  },
  stop: {
    kind: 'event',
    event: { type: 'session_stopped', reason: 'unknown' },
  },
  pause: { kind: 'confirm', command: 'pause' },
  resume: { kind: 'confirm', command: 'resume' },
};

const invokeNativeCommand = (
  command: SessionCommand,
  config: PitchTrackerConfig,
): Promise<unknown> => {
  switch (command) {
    case 'start':
      return native.invoke('startPractice', config, START_INVOKE_OPTIONS);
    case 'stop':
      return native.invoke('stopPractice');
    case 'pause':
      return native.invoke('pausePractice');
    case 'resume':
      return native.invoke('resumePractice');
  }
};

const invokeBridgeCommand = (
  bridge: PracticeBridge,
  command: SessionCommand,
  config: PitchTrackerConfig,
): Promise<unknown> => {
  switch (command) {
    case 'start':
      return bridge.startPractice(config);
    case 'stop':
      return bridge.stopPractice();
    case 'pause':
      return bridge.pausePractice();
    case 'resume':
      return bridge.resumePractice();
  }
};

export const usePitchSession = (
  options: UsePitchSessionOptions = {},
): UsePitchSession => {
  const [state, setState] = useState<SessionMachineState>(initialSessionState);
  const [fps, setFps] = useState(0);

  const stateRef = useRef(initialSessionState);
  const bridgeRef = useRef<PracticeBridge | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const frameTimesRef = useRef<number[]>([]);
  const onEventRef = useRef<(event: PitchTrackerEvent) => void>(
    () => undefined,
  );
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // isNative is fixed for the hook's lifetime — the transport can't change mid-session.
  const isNativeRef = useRef(options.isNative ?? isRunningInNativeApp());

  // Single source of truth: compute from the ref (never a stale closure) so a
  // double-click in one tick already sees the pending status and is guarded out.
  const apply = (input: SessionInput): SessionMachineState => {
    const next = sessionReducer(stateRef.current, input);
    if (next !== stateRef.current) {
      stateRef.current = next;
      setState(next);
    }
    return next;
  };

  const clearTimer = (): void => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const recover = (command: SessionCommand): void => {
    clearTimer();
    // Only un-wedge if still parked in this command's pending state; a real
    // confirmation may have already settled it.
    if (stateRef.current.status !== PENDING_BY_COMMAND[command]) return;
    apply(RECOVERY_INPUT[command]);
  };

  const recordFrame = (): void => {
    const now = optionsRef.current.now ?? Date.now;
    const at = now();
    const times = frameTimesRef.current;
    times.push(at);
    const cutoff = at - FPS_WINDOW_MS;
    while (times.length > 0 && times[0] < cutoff) times.shift();
    setFps(times.length);
  };

  onEventRef.current = (event: PitchTrackerEvent): void => {
    // A native lifecycle event is the authoritative confirmation → drop the safety timer.
    if (
      event.type === 'session_started' ||
      event.type === 'session_stopped' ||
      event.type === 'error'
    ) {
      clearTimer();
    }
    if (event.type === 'pitch_frame') recordFrame();
    apply({ kind: 'event', event });
  };

  // Wire the event source once: native push channel, or a mock bridge in the browser.
  useEffect(() => {
    const handler = (event: PitchTrackerEvent): void =>
      onEventRef.current(event);
    if (isNativeRef.current) {
      const off = native.on('pitchTrackerEvent', handler);
      return () => {
        clearTimer();
        off();
      };
    }
    const factory =
      optionsRef.current.createModule ?? createMockPitchTrackerModule;
    // 브라우저에서는 이 훅이 유일한 mock 이벤트 소유자다. 네이티브 채널로도 밀어 줘야
    // 그 채널을 직접 구독하는 화면(디버그 콘솔의 그래프·리드아웃·레벨 미터)이 값을 본다 —
    // 실기기에서는 셸이 하는 일이라, 안 하면 브라우저에서만 조용히 빈 화면이 된다.
    const browserHandler = (event: PitchTrackerEvent): void => {
      handler(event);
      forwardToNativeChannel(event);
    };
    const bridge = createPracticeBridge(browserHandler, factory());
    bridgeRef.current = bridge;
    return () => {
      clearTimer();
      void bridge.stopPractice();
      bridge.dispose();
      bridgeRef.current = null;
    };
    // Mount-once: transport identity is fixed; live values are read via refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = (command: SessionCommand, config?: PitchTrackerConfig): void => {
    // Synchronous guard — blocks multi-click races and conflicting commands while
    // any transition is in flight (sessionCan is all-false for pending statuses).
    if (!sessionCan(stateRef.current.status)[command]) return;
    apply({ kind: 'command', command });

    const cfg =
      config ?? optionsRef.current.config ?? createPitchTrackerConfig();
    const { confirmTimeoutMs } = optionsRef.current;
    const timeoutMs =
      confirmTimeoutMs ??
      (command === 'start'
        ? START_CONFIRM_TIMEOUT_MS
        : DEFAULT_CONFIRM_TIMEOUT_MS);
    clearTimer();
    timerRef.current = setTimeout(() => recover(command), timeoutMs);

    const bridge = bridgeRef.current;
    const promise = isNativeRef.current
      ? invokeNativeCommand(command, cfg)
      : bridge
        ? invokeBridgeCommand(bridge, command, cfg)
        : Promise.reject(new Error('usePitchSession: bridge not ready'));

    promise
      .then(() => {
        // Releases pause/resume (no push event); a no-op for start/stop already
        // settled by their native lifecycle event.
        clearTimer();
        apply({ kind: 'confirm', command });
      })
      .catch(() => recover(command));
  };

  return {
    state: state.status,
    phase: PHASE_BY_STATUS[state.status],
    can: sessionCan(state.status),
    start: (config?: PitchTrackerConfig) => send('start', config),
    stop: () => send('stop'),
    pause: () => send('pause'),
    resume: () => send('resume'),
    fps,
    captureSampleRate: state.captureSampleRate,
    isNative: isNativeRef.current,
  };
};
